import { ActionItem, WorkSummary } from '../../../interfaces/dashboard.interface';
import { PresetKey } from '../registry/presets';
import { compactMoney } from '../charts/axisPadding';
import { fmtMoney } from '../registry/format';
import type { KpiTile } from './BoardKpiStrip';

/**
 * Per-role headline tiles and Action Center items built from the work-summary endpoint.
 * A null field (the caller may not view that source, or there is nothing to date) produces NO tile
 * and NO item: it is never rendered as 0.
 */

/** Tile keys per role board, in display order. `ws.*` come from the work summary, the rest from the module loaders. */
export const PRESET_TILE_ORDER: Partial<Record<PresetKey, string[]>> = {
    manager: ['att.present', 'att.absent', 'ws.approvals', 'leave.pending', 'exp.pending', 'req.pending', 'inv.low', 'sales.pipeline'],
    accountant: ['acc.cash', 'acc.ar', 'ws.ar60', 'acc.ap', 'ws.billsDue7', 'ws.journalsAwaiting', 'ws.unreconciled', 'ws.nextDeadline'],
    apClerk: ['ws.billsDue7', 'ws.overdueBills', 'ws.billsHold', 'ws.runsDraft', 'acc.ap', 'ws.issuedCheques'],
    arClerk: ['acc.ar', 'ws.ar60', 'ws.overdueInvoices', 'ws.promises', 'ws.receivedPdc', 'sales.mtd'],
    paymentApprover: ['ws.runsAwaiting', 'ws.approvals', 'ws.journalsAwaiting', 'acc.cash', 'ws.billsDue7'],
    auditor: ['ws.journalsPosted', 'ws.journalsReversed', 'acc.cash', 'acc.ar', 'acc.ap', 'ws.lastClosed'],
};

/** Presets whose strip and Action Center are role-specific (owner, basic and the admin wildcard keep the whole-business ones). */
export const isRolePreset = (p: PresetKey | undefined): p is PresetKey => !!p && !!PRESET_TILE_ORDER[p];

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Today in Asia/Dhaka as yyyy-mm-dd. */
export function dhakaToday(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function daysUntil(isoDate: string, today: string): number {
    const [y1, m1, d1] = isoDate.split('-').map(Number);
    const [y2, m2, d2] = today.split('-').map(Number);
    return Math.round((Date.UTC(y1, m1 - 1, d1) - Date.UTC(y2, m2 - 1, d2)) / 86400000);
}

const num = (v: number | null | undefined): v is number => typeof v === 'number';

const countTile = (key: string, label: string, v: number | null | undefined, sub?: string, warn = true): KpiTile | null =>
    num(v) ? { key, label, value: v, sub, tone: warn && v > 0 ? 'warning' : 'default' } : null;

/** All work-summary tiles (unordered); the caller sorts and trims by the preset's order. */
export function buildWorkTiles(ws: WorkSummary | null, today: string = dhakaToday()): KpiTile[] {
    if (!ws) return [];
    const out: Array<KpiTile | null> = [
        countTile('ws.approvals', 'Approvals waiting for me', ws.approvalsWaitingForMe),
        countTile('ws.journalsAwaiting', 'Journals awaiting posting', ws.journalsAwaitingPosting),
        countTile('ws.journalsPosted', 'Journals posted this month', ws.journalsPostedThisMonth, undefined, false),
        countTile('ws.journalsReversed', 'Reversed journals this month', ws.journalsReversedThisMonth),
        countTile('ws.billsDue7', 'Bills due in 7 days', ws.billsDueIn7Days),
        countTile('ws.overdueBills', 'Overdue bills', ws.overdueBills),
        countTile('ws.billsHold', 'Bills on approval hold', ws.billsOnApprovalHold),
        countTile('ws.runsDraft', 'Payment runs in draft', ws.paymentRunsDraft),
        num(ws.paymentRunsAwaitingApproval) ? {
            key: 'ws.runsAwaiting', label: 'Payment runs waiting for approval', value: ws.paymentRunsAwaitingApproval,
            sub: num(ws.paymentRunsAwaitingApprovalAmount) ? `Net ${fmtMoney(ws.paymentRunsAwaitingApprovalAmount)}` : undefined,
            tone: ws.paymentRunsAwaitingApproval > 0 ? 'warning' : 'default',
        } : null,
        countTile('ws.issuedCheques', 'Issued cheques due in 7 days', ws.issuedChequesDueIn7Days),
        countTile('ws.receivedPdc', 'Received cheques (PDC) due in 7 days', ws.receivedPdcDueIn7Days),
        countTile('ws.overdueInvoices', 'Overdue invoices', ws.overdueInvoices),
        num(ws.receivableOver60Days) ? {
            key: 'ws.ar60', label: 'Receivable > 60 days', value: compactMoney(ws.receivableOver60Days),
            tone: ws.receivableOver60Days > 0 ? 'warning' : 'default',
        } : null,
        countTile('ws.promises', 'Promises to pay due in 7 days', ws.promisesToPayDueIn7Days),
        countTile('ws.unreconciled', 'Unreconciled bank lines', ws.unreconciledBankLines),
    ];
    if (ws.nextComplianceDeadline) {
        const d = daysUntil(ws.nextComplianceDeadline.dueDate, today);
        out.push({
            key: 'ws.nextDeadline', label: 'Next compliance deadline', value: `${d}d`,
            sub: `${ws.nextComplianceDeadline.label} · ${ws.nextComplianceDeadline.dueDate}`, tone: d <= 7 ? 'warning' : 'default',
        });
    }
    if (ws.lastClosedPeriod) {
        out.push({ key: 'ws.lastClosed', label: 'Last closed period', value: ws.lastClosedPeriod, sub: 'Books closed through' });
    }
    return out.filter((t): t is KpiTile => t !== null);
}

const item = (id: string, module: string, title: string, count: number, noun: string, detail: string, actionLabel: string, link: string,
    severity: ActionItem['severity'] = 'WARNING'): ActionItem => ({
    id, module, title, count, detail: `${plural(count, noun)} ${detail}`, actionLabel, link, severity,
});

/** Action Center items for a role board, from the work summary. Null or zero counts produce nothing. */
export function buildWorkActions(preset: PresetKey, ws: WorkSummary | null, today: string = dhakaToday()): ActionItem[] {
    if (!ws) return [];
    const pos = (v: number | null | undefined): v is number => num(v) && v > 0;
    const a: ActionItem[] = [];
    const approvals = () => { if (pos(ws.approvalsWaitingForMe)) a.push(item('approvals.waiting', 'approval', 'Approvals waiting for you', ws.approvalsWaitingForMe, 'request', 'in your approval inbox', 'Open approvals', '/approvals')); };
    const journals = () => { if (pos(ws.journalsAwaitingPosting)) a.push(item('journal.awaiting', 'accounting', 'Journal entries awaiting posting', ws.journalsAwaitingPosting, 'journal', 'still in draft', 'Review journals', '/accounting/journal-entries')); };
    const overdueBills = () => { if (pos(ws.overdueBills)) a.push(item('bill.overdue', 'procurement', 'Overdue supplier bills', ws.overdueBills, 'bill', 'past the due date', 'View bills', '/procurement/supplier-bills', 'CRITICAL')); };
    const holds = () => { if (pos(ws.billsOnApprovalHold)) a.push(item('bill.hold', 'procurement', 'Bills on approval hold', ws.billsOnApprovalHold, 'bill', 'waiting for approval', 'View bills', '/procurement/supplier-bills')); };

    switch (preset) {
        case 'accountant':
            journals();
            if (pos(ws.unreconciledBankLines)) a.push(item('bank.unreconciled', 'banking', 'Bank lines to reconcile', ws.unreconciledBankLines, 'line', 'not matched yet', 'Reconcile', '/banking/unreconciled'));
            if (ws.nextComplianceDeadline) {
                const d = daysUntil(ws.nextComplianceDeadline.dueDate, today);
                if (d <= 7) a.push({ id: 'compliance.due', severity: d <= 2 ? 'CRITICAL' : 'WARNING', module: 'accounting', title: 'Compliance deadline coming up', detail: `${ws.nextComplianceDeadline.label} is due ${ws.nextComplianceDeadline.dueDate}`, count: 0, actionLabel: 'Open calendar', link: '/accounting/compliance-calendar' });
            }
            holds();
            if (num(ws.receivableOver60Days) && ws.receivableOver60Days > 0) a.push({ id: 'ar.over60', severity: 'WARNING', module: 'accounting', title: 'Receivables more than 60 days overdue', detail: `${compactMoney(ws.receivableOver60Days)} still unpaid`, count: 0, actionLabel: 'View invoices', link: '/sales/invoices' });
            break;
        case 'apClerk':
            overdueBills();
            holds();
            if (pos(ws.paymentRunsDraft)) a.push(item('run.draft', 'procurement', 'Payment runs in draft', ws.paymentRunsDraft, 'run', 'not yet submitted', 'Open payment runs', '/procurement/payment-runs'));
            break;
        case 'arClerk':
            if (pos(ws.overdueInvoices)) a.push(item('invoice.overdue', 'sales', 'Overdue invoices', ws.overdueInvoices, 'invoice', 'past the due date', 'View invoices', '/sales/invoices', 'CRITICAL'));
            if (pos(ws.promisesToPayDueIn7Days)) a.push(item('promise.due', 'sales', 'Promises to pay falling due', ws.promisesToPayDueIn7Days, 'promise', 'due within 7 days', 'Open collections', '/sales/collections'));
            if (pos(ws.receivedPdcDueIn7Days)) a.push(item('pdc.due', 'banking', 'Post-dated cheques to deposit', ws.receivedPdcDueIn7Days, 'cheque', 'due within 7 days', 'Open cheques', '/banking/cheques'));
            break;
        case 'paymentApprover':
            if (pos(ws.paymentRunsAwaitingApproval)) a.push(item('run.awaiting', 'procurement', 'Payment runs to approve', ws.paymentRunsAwaitingApproval, 'run', 'waiting for your approval', 'Open payment runs', '/procurement/payment-runs'));
            approvals();
            journals();
            break;
        case 'manager':
            approvals();
            break;
        default:
            break;
    }
    return a;
}
